import React, { useState } from "react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { useTenant } from "@/lib/TenantContext";
import { tenantSiteUrl } from "../../../lib/tenantHost";
import { crmErrorMessage } from "../api/crmErrors";
import useCaptureForm from "../hooks/useCaptureForm";
import CrmPageHeader from "../ui/CrmPageHeader";
import CrmCard from "../ui/CrmCard";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import FormFieldsCard from "../forms/FormFieldsCard";
import CaptureFormView from "../forms/CaptureFormView";
import FormPreviewDialog from "../forms/FormPreviewDialog";
import { CRM } from "../../../constants/testIds/crm";

const hostOf = (url) => { try { return new URL(url).host; } catch { return ""; } };

/**
 * Admin → CRM → Forms (spec Screen 4 / design §E.4): the ONE default capture form every
 * practice gets at signup — its fields, its consent text and the embed snippet for the
 * practice's own site — with a live preview beside the editor on wide screens.
 */
export default function CrmForms() {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { tenant } = useTenant();
  const { form, loadError, reload, update } = useCaptureForm();
  const [preview, setPreview] = useState(false);
  const practiceName = tenant?.name || "";
  const host = hostOf(tenantSiteUrl(tenant));

  const save = (patch, doneKey) => update(patch)
    .then(() => { if (doneKey) toast.success(t(doneKey)); })
    .catch((e) => toast.error(crmErrorMessage(e, t, tc)));

  return (
    <div data-testid={CRM.forms} className="space-y-4">
      <CrmPageHeader
        title={t("forms.title")}
        actions={form && (
          <CrmButton variant="outline" onClick={() => setPreview(true)} data-testid={CRM.formsPreview}>
            {t("forms.preview")}
          </CrmButton>
        )}
      />
      {loadError && <CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.formsError} />}
      {form && (
        <p data-testid={CRM.formsEmailVerificationNote} className="text-xs text-eh-muted">
          {t("forms.emailVerificationNote")}
        </p>
      )}
      {form && (
        <div className="grid gap-6 md:grid-cols-2">
          <CrmCard>
            <FormFieldsCard
              form={form}
              submissionsCount={form.submissions_count}
              onFieldChange={(fields) => save({ fields }, "forms.saved")}
              onConsentChange={(consent_text) => save({ consent_text }, "forms.consentSaved")}
            />
          </CrmCard>
          <div className="hidden md:block">
            <CaptureFormView
              practiceName={practiceName}
              host={host}
              fields={form.fields}
              consentText={form.consent_text?.[lang] || form.consent_text?.en || ""}
            />
          </div>
        </div>
      )}
      {preview && (
        <FormPreviewDialog open={preview} onClose={() => setPreview(false)} practiceName={practiceName} host={host} form={form} />
      )}
    </div>
  );
}
